// History-backed navigation state for the SPA.
//
// Normally the app's path and query are the page URL's. A build with
// VITE_ROUTER=hash (the static demo, `pnpm build:demo`) keeps them in the
// hash instead (`index.html#/gh/owner/repo/pull/1/files?file=…`), for hosts
// that serve only index.html.
import { formerAlias, githubAlias, isRoutableHref, matchRoute, type Route } from './router';

/** True in hash-routed builds. */
export const HASH_ROUTING = import.meta.env.VITE_ROUTER === 'hash';

/** The app's current path and query string (with `?`, or empty). */
export function appLocation(): { pathname: string; search: string } {
  if (!HASH_ROUTING) return { pathname: location.pathname, search: location.search };
  const raw = location.hash.replace(/^#/, '') || '/';
  const url = new URL(raw.startsWith('/') ? raw : '/' + raw, 'http://app');
  return { pathname: url.pathname, search: url.search };
}

function toUrl(pathAndQuery: string): string {
  return HASH_ROUTING ? location.pathname + location.search + '#' + pathAndQuery : pathAndQuery;
}

/**
 * Replace one of GitHub's other pull request URLs (see `githubAlias`), or
 * the URL of a view the Review view replaced (see `formerAlias`), in the
 * address bar with the changelist URL showing the same thing.
 */
function canonicalize() {
  const { pathname, search } = appLocation();
  const alias = githubAlias(pathname, search) ?? formerAlias(pathname, search);
  if (alias) history.replaceState(history.state, '', toUrl(alias));
}

canonicalize();

/**
 * Told on the window whenever the app changes its own URL (`history`
 * announces only Back and Forward). Every `Nav` reads the URL again on it,
 * so the page shown is the URL's whichever instance changed it: a hot
 * update of this module evaluates it again, and the instance before it
 * still hears the clicks first.
 */
const NAVIGATED = 'changelist:navigated';

/** Kept with a history entry the app pushed (see `Nav.back`). */
const PUSHED = 'pushed';

class Nav {
  pathname = $state(appLocation().pathname);
  search = $state(appLocation().search);
  route: Route = $derived(matchRoute(this.pathname));
  query: URLSearchParams = $derived(new URLSearchParams(this.search));

  constructor() {
    window.addEventListener('popstate', () => this.sync());
    window.addEventListener(NAVIGATED, () => this.sync());
    if (HASH_ROUTING) window.addEventListener('hashchange', () => this.sync());
    document.addEventListener('click', (e) => this.intercept(e));
  }

  private sync() {
    canonicalize();
    const { pathname, search } = appLocation();
    this.pathname = pathname;
    this.search = search;
  }

  /** Navigate to a same-origin app path (with optional query). */
  go(href: string, opts: { replace?: boolean; keepScroll?: boolean } = {}) {
    const here = appLocation();
    const url = new URL(href, 'http://app' + here.pathname + here.search);
    const samePath = url.pathname === here.pathname;
    const target = toUrl(url.pathname + url.search);
    if (opts.replace) history.replaceState(history.state, '', target);
    else history.pushState({ [PUSHED]: true }, '', target);
    window.dispatchEvent(new Event(NAVIGATED));
    if (!samePath && !opts.keepScroll) window.scrollTo(0, 0);
  }

  /**
   * Leave the page shown for the one before it: Back, when the app pushed
   * the entry shown (so the entry before it is the app's); else `fallback`,
   * for a page that was opened by its URL.
   */
  back(fallback = '/') {
    if (this.entry<boolean>(PUSHED)) history.back();
    else this.go(fallback);
  }

  /**
   * A value kept with the current history entry (not in the URL): what a
   * view restores when Back or Forward returns to the entry. A new entry
   * starts with none.
   */
  entry<T>(key: string): T | undefined {
    const st = history.state as Record<string, unknown> | null;
    return st && typeof st === 'object' ? (st[key] as T | undefined) : undefined;
  }

  /** Keep `value` with the current history entry under `key` (see `entry`). */
  setEntry(key: string, value: unknown) {
    const st = history.state && typeof history.state === 'object' ? history.state : {};
    if (st[key] === value) return;
    history.replaceState({ ...st, [key]: value }, '');
  }

  /** Update query parameters on the current path. `null` deletes a key. */
  setQuery(params: Record<string, string | null>, opts: { replace?: boolean } = {}) {
    const here = appLocation();
    const url = new URL('http://app' + here.pathname + here.search);
    for (const [k, v] of Object.entries(params)) {
      if (v === null) url.searchParams.delete(k);
      else url.searchParams.set(k, v);
    }
    if (url.search === here.search) return;
    this.go(url.pathname + url.search, { ...opts, keepScroll: true });
  }

  private intercept(e: MouseEvent) {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const a = (e.target as Element | null)?.closest?.('a');
    if (!a || a.target || a.hasAttribute('download') || a.dataset.external !== undefined) return;
    const href = a.getAttribute('href');
    if (!href || href.startsWith('#')) return;
    if (!isRoutableHref(a.href, location.origin)) return;
    e.preventDefault();
    const url = new URL(a.href);
    this.go(url.pathname + url.search);
  }
}

export const nav = new Nav();
